import { useEffect, useSyncExternalStore } from 'react';
import { usePreferencesStore } from '@/entities/preferencesStore';
import type { CornerStyle } from '@/shared/types';

// Guarded so tests (jsdom has no matchMedia by default) and any non-browser
// context degrade to light instead of throwing.
const prefersDarkQuery = (): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function subscribeSystem(callback: () => void): () => void {
  const mq = prefersDarkQuery();
  if (!mq) return () => {};
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

/**
 * The theme actually in effect right now: the user's explicit choice, or the
 * OS preference when the stored choice is 'system'. Reactive to OS changes.
 */
export function useResolvedTheme(): 'light' | 'dark' {
  const theme = usePreferencesStore((s) => s.theme);
  const systemDark = useSyncExternalStore(
    subscribeSystem,
    () => prefersDarkQuery()?.matches ?? false,
    () => false,
  );
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  return systemDark ? 'dark' : 'light';
}

/** The corner preference, as stored. There is nothing to resolve. */
export function useCornerStyle(): CornerStyle {
  return usePreferencesStore((s) => s.cornerStyle);
}

/**
 * A chart bar's cap radius: the value the chart chose, or 0 in square mode.
 *
 * This is the only thing in the app that needs the corner preference as a
 * number. Everything else — including inline `style` objects and the string
 * that builds the market tooltip — is CSS in the end and reads
 * `var(--corner-radius)` directly. Recharts is the exception: `<Bar radius>`
 * is a JS prop it compiles into an SVG path, so no variable can reach it, and
 * those eight bar charts would have kept their caps and been the only curve
 * left on the screen in square mode. `rx` on an SVG <rect> is the same case.
 *
 * It takes the rounded-mode value rather than a scale step because bar caps
 * are 1px and 2px — picked per chart against its own bar width, not from the
 * chrome scale, and not worth pretending otherwise.
 */
export function useChartRadius(rounded: number): number {
  return useCornerStyle() === 'square' ? 0 : rounded;
}

/**
 * Applies the resolved theme to the document root by toggling the `.dark`
 * class, and the corner preference by toggling `.square`. Call once, high in
 * the tree (AppLayout). The app ships a full `.dark` token set in styles.css
 * that nothing was toggling until this was wired.
 *
 * Both classes go on the SAME element, `<html>`, because `index.html`'s
 * pre-paint bootstrap has only `document.documentElement` to write to — it
 * runs before React exists. Keeping the two in step means a hydration never
 * moves a class the bootstrap already set, which is what a flash is.
 */
export function useApplyTheme(): void {
  const resolved = useResolvedTheme();
  const cornerStyle = useCornerStyle();
  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }, [resolved]);
  useEffect(() => {
    document.documentElement.classList.toggle(
      'square',
      cornerStyle === 'square',
    );
  }, [cornerStyle]);
}
